import { autoinject, TaskQueue, bindable } from 'aurelia-framework';
import { ComponentBase, IForm, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import * as UM from 'app/um/models/essette-api';
import { validate } from 'app/shared/elements/ess-rule';
import { SaveCareDayStatusReason } from 'app/um/components/care-day-statuses/save-care-day-status-reason';
import { IPopup } from 'app/shared/elements/popup-base';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { AdmissionCareDayService } from 'app/um/services/admission-care-day-service';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { ExpandEvent, EssTableCustomElement } from 'app/shared/elements/ess-table';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { Toaster } from 'app/shared/lib/toaster';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { localSort } from 'app/shared/lib/sorting';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { CareDayStatusReasonReloadEvent } from 'app/um/lib/events';

const expandedReasonsCache = new Map<string, Array<UM.AdmissionCareDayStatus>>();

@autoinject()
export class CareDayStatusReasons extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected expandedCacheKey: string = "um-manage-careDay-statuses-reasons";

    @bindable
    protected rememberExpand: boolean = true;

    private careDayStatusReasonReload: Subscription | null = null;

    protected careDayStatuses: ReadonlyArray<UM.AdmissionCareDayStatus> | null = null;

    private sorting: Sorting = new Sorting();
    private localSorting: Sorting = new Sorting();
    protected resultCount: number | null = 0;
    protected pageSize: number = 25;
    protected includeInactive: boolean = false;

    protected validate = validate;

    protected editedStatusReason: UM.AdmissionCareDayStatusReason | null = null;

    ////****************** Element References ***************************************////
    protected $table!: EssTableCustomElement<UM.AdmissionCareDayStatus[]>;
    protected $pager!: EssPagerCustomElement;

    // Save Status Reason Modal
    protected $saveStatusReasonModal!: IPopup;
    protected $saveStatusReasonForm!: IForm;
    protected $saveStatusReasonFormElement!: Element;
    protected $saveStatusReason!: SaveCareDayStatusReason;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AdmissionCareDayService,
        private readonly queue: TaskQueue,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
        // Default Sort
        this.sorting.entries = [
            {
                reference: 'description',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(): void {
        this.reload();
    }

    protected attached(): void {
        this.careDayStatusReasonReload = this.events.subscribe(CareDayStatusReasonReloadEvent, (e: CareDayStatusReasonReloadEvent) => {
            this.reload();
        });
    }

    protected detached(): void {
        if(this.careDayStatusReasonReload) {
            this.careDayStatusReasonReload.dispose();
        }
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        if(this.sorting.entries.length == 0) {
            this.reload();
        };
    }

    /** Handles the grid's sort event for the detail tables */
    protected applyLocalSort(event: SortChangeEvent, reasons: (UM.AdmissionCareDayStatusReason)[]): void {
        const { property, direction } = event.detail;
        const { localSorting } = this;
        if(direction === null) {
            localSorting.remove(property);
        } else {
            localSorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        localSort(reasons, localSorting);
        if(this.localSorting.entries.length == 0) {
            this.reload();
        };
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadAdmissionCareDayStatuses();
    }

    // #region Save Status Reason Modal Functions
    protected saveStatusReason(statusReason?: UM.AdmissionCareDayStatusReason): void {
        this.editedStatusReason = statusReason ?? null;
        this.$saveStatusReasonModal.open();
    }

    protected saveStatusReasonClose(): void {
        if(this.$saveStatusReasonForm.reset) this.$saveStatusReasonForm.reset();
        this.$saveStatusReasonModal.close();
    }

    protected async saveStatusReasonFormSuccess(): Promise<void> {
        this.saveStatusReasonClose();
        await this.reload();
    }

    // #endregion Save Status Reason Modal Functions

    private restoreExpanded() {
        if(!this.rememberExpand) { return; }
        // we need to give aurelia a chance to bind the reasons into the
        // table before we ask the table to expand the rows
        this.queue.queueTask(() => {
            if(!this.expandedCacheKey) { return; }
            const expandedReasons = expandedReasonsCache.get(this.expandedCacheKey);
            if(expandedReasons !== undefined) {
                this.$table.restoreExpanded(expandedReasons);
            }
        });
    }

    protected trackExpanded({ detail }: ExpandEvent<UM.AdmissionCareDayStatus>) {
        if(!this.rememberExpand || !this.expandedCacheKey) { return; }
        expandedReasonsCache.set(this.expandedCacheKey, detail.items);
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async activateCareDayStatusReason(careDayStatusReason: UM.AdmissionCareDayStatusReason): Promise<void> {
        // Show confirmation dialog only if deactivating
        if(careDayStatusReason.isActive) {
            const result = await confirmation.showDeactivate('um:auth.careDays.status.reason.$long', 1);
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }

            if(!(result.dialogResponse === confirmation.options.OK)) {
                return;
            }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateCareDayStatusReason(careDayStatusReason.id!, !careDayStatusReason.isActive);
            isSuccessful = response.isSuccessful;
            this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('um:auth.careDays.status.reason.$long', !careDayStatusReason.isActive, isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    private async loadAdmissionCareDayStatuses(): Promise<void> {

        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.sorting,
            includeInactive: this.includeInactive
        };

        const includes: string[] = [];

        try {
            this.state.beginLoad();

            const response = await this.service.loadCareDayStatuses(options, includes, true);
            this.careDayStatuses = response.results;
            this.resultCount = response.count;
            this.restoreExpanded();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
